<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/conversations';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [String, Number], required: true },
});

const emit = defineEmits([
  'cancel',
  'manageAppointment',
  'updated',
  'update:show',
]);
const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const moduleName = ref('');
const availableFields = ref([]);
const readOnlyFields = ref([]);
const requiredFields = ref([]);
const statusOptions = ref([]);
const record = reactive({
  first_name: '',
  last_name: '',
  primary_phone: '',
  additional_phone: '',
  email: '',
  status: '',
  origin: '',
});

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});
const moduleLabel = computed(() =>
  moduleName.value === 'Contacts'
    ? t('CONVERSATION.VTIGER_RECORD.CONTACT')
    : t('CONVERSATION.VTIGER_RECORD.LEAD')
);
const hasField = field => availableFields.value.includes(field);
const hasReadOnlyField = field => readOnlyFields.value.includes(field);
const errorMessage = error =>
  error?.response?.data?.error || t('CONVERSATION.VTIGER_RECORD.ERROR');

const reset = () => {
  moduleName.value = '';
  availableFields.value = [];
  readOnlyFields.value = [];
  requiredFields.value = [];
  statusOptions.value = [];
  Object.assign(record, {
    first_name: '',
    last_name: '',
    primary_phone: '',
    additional_phone: '',
    email: '',
    status: '',
    origin: '',
  });
};

const loadRecord = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.getVtigerRecord(
      props.conversationId
    );
    moduleName.value = data.module;
    availableFields.value = data.available_fields || [];
    readOnlyFields.value = data.read_only_fields || [];
    requiredFields.value = data.required_fields || [];
    statusOptions.value = data.status_options || [];
    Object.assign(record, data.fields || {});
  } catch (error) {
    useAlert(errorMessage(error));
    localShow.value = false;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.show,
  async isOpen => {
    if (!isOpen) return;
    reset();
    await loadRecord();
  }
);

const close = () => {
  if (isSaving.value) return;
  localShow.value = false;
  emit('cancel');
};

const manageAppointment = () => {
  if (isSaving.value || isLoading.value) return;
  localShow.value = false;
  emit('manageAppointment');
};

const save = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    const payload = Object.fromEntries(
      availableFields.value.map(field => [field, record[field] ?? ''])
    );
    const { data } = await ConversationApi.updateVtigerRecord(
      props.conversationId,
      payload
    );
    useAlert(
      t('CONVERSATION.VTIGER_RECORD.SUCCESS', { module: moduleLabel.value })
    );
    localShow.value = false;
    emit('updated', data);
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="close"
    :close-on-backdrop-click="!isSaving"
    class="!items-start [&>div]:!top-12 [&>div]:sticky"
  >
    <form class="flex w-full flex-col gap-5 px-6 py-6" @submit.prevent="save">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONVERSATION.VTIGER_RECORD.TITLE', { module: moduleLabel }) }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.DESCRIPTION') }}
        </p>
        <p v-if="moduleName" class="mt-1 text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.MODULE', { module: moduleName }) }}
        </p>
      </div>

      <div v-if="isLoading" class="py-8 text-center text-sm text-n-slate-11">
        {{ t('CONVERSATION.VTIGER_RECORD.LOADING') }}
      </div>

      <template v-else>
        <label
          v-if="hasField('first_name')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.FIRST_NAME') }}
          <input
            v-model.trim="record.first_name"
            :required="requiredFields.includes('first_name')"
            type="text"
            class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <label
          v-if="hasField('last_name')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.LAST_NAME') }}
          <input
            v-model.trim="record.last_name"
            :required="requiredFields.includes('last_name')"
            type="text"
            class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <label
          v-if="hasReadOnlyField('primary_phone')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.PRIMARY_PHONE') }}
          <input
            :value="record.primary_phone"
            readonly
            type="tel"
            class="!mb-0 !h-10 !w-full cursor-not-allowed !rounded-lg !bg-n-alpha-2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
          <span class="text-xs font-normal text-n-slate-11">
            {{ t('CONVERSATION.VTIGER_RECORD.PRIMARY_PHONE_HELP') }}
          </span>
        </label>

        <label
          v-if="hasReadOnlyField('origin')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.ORIGIN') }}
          <input
            :value="record.origin"
            readonly
            type="text"
            class="!mb-0 !h-10 !w-full cursor-not-allowed !rounded-lg !bg-n-alpha-2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <label
          v-if="hasField('additional_phone')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.ADDITIONAL_PHONE') }}
          <input
            v-model.trim="record.additional_phone"
            type="tel"
            :placeholder="t('CONVERSATION.VTIGER_RECORD.PHONE_PLACEHOLDER')"
            class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <label
          v-if="hasField('email')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.VTIGER_RECORD.EMAIL') }}
          <input
            v-model.trim="record.email"
            :required="requiredFields.includes('email')"
            type="email"
            class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <label
          v-if="hasField('status')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{
            moduleName === 'Contacts'
              ? t('CONVERSATION.VTIGER_RECORD.CONTACT_STATUS')
              : t('CONVERSATION.VTIGER_RECORD.STATUS')
          }}
          <select
            v-model="record.status"
            :required="requiredFields.includes('status')"
            class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          >
            <option
              v-for="option in statusOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </label>

        <label
          v-if="hasReadOnlyField('status')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{
            moduleName === 'Contacts'
              ? t('CONVERSATION.VTIGER_RECORD.CONTACT_STATUS')
              : t('CONVERSATION.VTIGER_RECORD.STATUS')
          }}
          <input
            :value="record.status"
            readonly
            type="text"
            class="!mb-0 !h-10 !w-full cursor-not-allowed !rounded-lg !bg-n-alpha-2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>

        <p class="text-xs text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.RESTRICTION') }}
        </p>
      </template>

      <div class="flex justify-end gap-2">
        <NextButton
          type="button"
          color="slate"
          faded
          icon="i-lucide-calendar-cog"
          :label="
            moduleName === 'Leads'
              ? t('CONVERSATION.APPOINTMENT.CONVERT_BUTTON')
              : t('CONVERSATION.APPOINTMENT.MANAGE_BUTTON')
          "
          :disabled="isLoading || isSaving || !moduleName"
          @click="manageAppointment"
        />
        <NextButton
          type="button"
          color="slate"
          faded
          :label="t('CONVERSATION.VTIGER_RECORD.CANCEL')"
          :disabled="isSaving"
          @click="close"
        />
        <NextButton
          type="submit"
          color="blue"
          :label="t('CONVERSATION.VTIGER_RECORD.SAVE')"
          :is-loading="isSaving"
          :disabled="isLoading || !availableFields.length"
        />
      </div>
    </form>
  </woot-modal>
</template>
